/* eslint-disable react/jsx-props-no-spreading */
import App from 'next/app';
import React from 'react';
import { ApolloProvider } from '@apollo/client';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { IntlProvider } from 'react-intl';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import { SnackbarProvider } from 'notistack';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { get, flowRight as compose } from 'lodash';
import { ThemeProvider as MUIThemeProvider, StyledEngineProvider } from '@mui/material';
import { Settings } from 'luxon';
import { NavigationProvider } from '../contexts/NavigationCtx';
import { getDefaultTheme, MUIBaseTheme, TypeProvider } from '../themes';
import DefaultLayout from '../layouts/DefaultLayout';
import { Favicon } from '../components/Favicon';
import { MaterialSSR } from '../lib/material-ui/MaterialSSR';
import ToastWrapper from '../components/ToastWrapper';
import { withApollo } from '../apollo';
import { getDefaultLocale, LocaleProvider, getActiveLocalFromContext } from '../locales';
import { getAuthStore, getVersionInfoStore } from '../hocs/withAuth/service';
import initApollo from '../apollo/initApollo';
import routes from '../routes/routes';
import { globalCss } from '@/customComponents/css/global';

const GlobalStyle = createGlobalStyle`
  ${globalCss}
`;

/**
 * Custom App component for the LodgeLink application.
 *
 * https://nextjs.org/docs/#custom-%3Capp%3E
 */

class LodgeLinkApp extends App {
  static async getInitialProps(props) {
    Settings.defaultZone = 'utc';
    const { Component } = props;
    let { ctx } = props;

    // Localization
    const locale = getActiveLocalFromContext(ctx);
    const localeMessages = LodgeLinkApp.getLocaleMessages(ctx);

    // Augment context with locale because it may be useful in getInitialProps on pages
    ctx = { locale, ...ctx };

    // Execute getInitialProps with our extended context
    let pageProps = {};
    if (Component.getInitialProps) {
      pageProps = await Component.getInitialProps(ctx);
    }

    const { name: routeName = 'error' } = routes.find((route) => `/${route.page}` === ctx?.pathname) ?? {};

    const unAuthRoutes = ['SignIn', 'ForgotPassword', 'ResetPassword', 'Token', 'ConfirmBooking', 'RejectBooking'];
    const noAuthRequired = unAuthRoutes.includes(routeName);

    const themeObj = getDefaultTheme();

    pageProps = { locale, routeName, ...pageProps };

    return { noAuthRequired, locale, localeMessages, pageProps, themeObj };
  }

  /**
   * Get localized messages.
   *
   * Pulled from this example:
   * https://github.com/zeit/next.js/blob/canary/examples/with-react-intl/pages/_app.js
   *
   * @param {Object} ctx
   */
  static getLocaleMessages(ctx) {
    const { req } = ctx;

    // On initial page load express sends through all of the translated messages on the request.
    // Used for server side rendering
    if (req?.localeMessages) {
      return req.localeMessages;
    }

    // After the initial page load messages are pulled from `__NEXT_DATA__`.
    // Used for client side rendering
    if (process.browser) {
      return get(window, '__NEXT_DATA__.props.localeMessages', {});
    }

    // Something went very wrong if you got here
    return {};
  }

  componentDidUpdate() {
    let authStore;
    let versionInfoStore;

    versionInfoStore = getVersionInfoStore();
    const cachedBuildId = versionInfoStore.get('buildId');
    const buildId = get(window, '__NEXT_DATA__.buildId', 'DEFAULT-BUILD-ID');
    const clearLocalStorage = process.env.CLEAR_LOCAL_STORAGE_ON_DEPLOYMENT === 'true';

    if (!cachedBuildId || !clearLocalStorage) {
      // No cached Build ID; create one
      versionInfoStore.set('buildId', buildId);
    } else if (cachedBuildId !== buildId && clearLocalStorage) {
      // Upon detecting a Build ID change, stash auth and clear storage
      // then recreate auth & version info stores and re-initialize Apollo client
      authStore = getAuthStore();
      const authStash = {
        token: authStore.get('token'),
        expiry: authStore.get('expiry'),
        authError: authStore.get('authError')
      };
      window.localStorage.clear();
      authStore = getAuthStore(true);
      authStore.set('token', authStash.token);
      authStore.set('expiry', authStash.expiry);
      authStore.set('authError', authStash.authError);
      versionInfoStore = getVersionInfoStore(true);
      versionInfoStore.set('buildId', buildId);
      initApollo({}, {}, true);
    }
  }

  render() {
    const {
      apolloClient,
      Component,
      locale = { id: 'en', label: 'Canada' },
      localeMessages,
      pageProps,
      themeObj,
      noAuthRequired
    } = this.props;

    return (
      <MaterialSSR>
        <StyledEngineProvider injectFirst>
          <MUIThemeProvider theme={MUIBaseTheme}>
            <SnackbarProvider style={{ isolation: 'isolate' }}>
              <ToastWrapper>
                <HelmetProvider>
                  <Favicon />
                  <LocaleProvider value={locale}>
                    <IntlProvider
                      defaultLocale={getDefaultLocale().id}
                      initialNow={Date.now()}
                      locale={locale.id}
                      messages={localeMessages}
                    >
                      <ApolloProvider client={apolloClient}>
                        <Helmet
                          htmlAttributes={{ lang: locale.id }}
                          meta={[
                            { charset: 'UTF-8' },
                            { 'http-equiv': 'Content-Language', content: locale.language },
                            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
                          ]}
                        />
                        <TypeProvider value={themeObj.id}>
                          <ThemeProvider theme={themeObj.theme}>
                            <>
                              <GlobalStyle />
                              {noAuthRequired && <Component {...pageProps} />}
                              {!noAuthRequired && (
                                <DndProvider backend={HTML5Backend}>
                                  <NavigationProvider>
                                    <DefaultLayout>
                                      <Component {...pageProps} />
                                    </DefaultLayout>
                                  </NavigationProvider>
                                </DndProvider>
                              )}
                            </>
                          </ThemeProvider>
                        </TypeProvider>
                      </ApolloProvider>
                    </IntlProvider>
                  </LocaleProvider>
                </HelmetProvider>
              </ToastWrapper>
            </SnackbarProvider>
          </MUIThemeProvider>
        </StyledEngineProvider>
      </MaterialSSR>
    );
  }
}

const ComposedLodgeLinkApp = compose(withApollo)(LodgeLinkApp);
export default ComposedLodgeLinkApp;
